import { integrationTesting } from '@ps/web-core-utils';
import type { PokerHomeGames } from '@ps/web-martech-cms-types';
import type { GetUserClubsAndScheduledGamesResponse } from '@ps/web-shared-api-types/v0/SwHomeGames';
import type { GetLobbyInformationRequest } from '@ps/web-shared-api-types/v1-preview/SwHomeGames';
import * as Sentry from '@sentry/react';
import { isEmpty } from 'lodash';

import { RequestError } from '@swf/core/errors/RequestError';
import type { ExtractDispatchFromModel, WidgetEffectParams } from '@swf/core/store';

import { PKR_LOADERS } from '../../constants';
import { ENDPOINTS } from '../../constants/endpoints';
import type { HomeGamesWidgetModel } from '../../model';

export const fetchLobbyData = async (
  { polling, initial, clubId }: { polling?: boolean; initial?: boolean; clubId?: number } = {},
  params: WidgetEffectParams<PokerHomeGames>
) => {
  if (integrationTesting && polling) return;

  const {
    request,
    dispatch,
    widget: { uid }
  } = params;
  const widgetDispatch: ExtractDispatchFromModel<HomeGamesWidgetModel> = dispatch[uid];

  try {
    const { body } = await request<GetUserClubsAndScheduledGamesResponse>({
      service: ENDPOINTS.lobby,
      body: { polling } as GetLobbyInformationRequest,
      http: true
    });
    const { success, clubInfoList, filteredClubInfoReply } = body;

    if (success && clubInfoList && filteredClubInfoReply) {
      const hasTables = !isEmpty(filteredClubInfoReply?.tables);
      const hasTourns = !isEmpty(filteredClubInfoReply?.tourns);

      widgetDispatch.setUserClubsAndGames(body);
      widgetDispatch.setError({ level: 'critical', value: false });

      if (hasTables) {
        widgetDispatch.fetchScheduledRingGames({ clubId, polling });
      }
      if (hasTourns) {
        widgetDispatch.fetchRegisteredTournaments({ clubId, polling });
      }
    }
  } catch (error) {
    const errorCode =
      error instanceof RequestError && error.headers?.statusCode
        ? Number(error.headers.statusCode)
        : 500;
    const isUnauthorisedRequest = errorCode === 401;

    if (isUnauthorisedRequest) {
      /*
        Due to a race condition with the 'starslogout' event and the modal dispatch we
        want to ensure that the modal is only called after the logout is complete
      */
      dispatch.auth.logout({ implicit: true });
      widgetDispatch.setError({ level: 'critical', value: true });
    }
    Sentry.addBreadcrumb({
      type: 'debug',
      category: 'lobby-data-error',
      message: (error as Error)?.message,
      data: error || {}
    });
  } finally {
    if (initial) {
      widgetDispatch.setLoading({ isLoading: false, loaderName: PKR_LOADERS.initial });
    }
  }
};
